import { Link } from "react-router-dom";
import { Heart, MapPin, BadgeCheck, Coins, Repeat, Timer, Flame } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/i18n";
import { formatPrice, formatKm } from "@/lib/format";
import { useNavigate } from "react-router-dom";

const Badge = ({ className, children, testid }) => <span data-testid={testid} className={`pill ${className}`}>{children}</span>;

export const isSponsored = (l) => { const n = new Date().toISOString(); return [l.promo?.top_until, l.promo?.highlight_until, l.promo?.showcase_until].some((d) => d && d > n); };

export const ListingBadges = ({ l, t, compact = false }) => {
  const extra = compact ? "hidden sm:inline-flex" : "";
  return (
  <>
    {isSponsored(l) && <Badge testid="badge-sponsored" className="bg-[#FFB020] text-[#1E1B2E]">{t("p4.sponsored")}</Badge>}
    {l.is_demo && <Badge testid="badge-demo" className="bg-[#1E1B2E] text-white">{t("card.demo")}</Badge>}
    {l.verified_by_juppo && <Badge testid="badge-verified" className="bg-[#3B1272] text-white"><BadgeCheck className="h-3 w-3" />{t("card.verified")}</Badge>}
    {l.coin_eligible && <Badge testid="badge-coin" className={`${extra} bg-amber-50 text-amber-700 ring-1 ring-amber-200`}><Coins className="h-3 w-3" />{t("card.coin")}</Badge>}
    {l.deal_ends_at && new Date(l.deal_ends_at) > new Date() && <Badge testid="badge-deal" className={`${extra} bg-[#FF7A6B] text-white`}><Timer className="h-3 w-3" />{t("card.deal")}</Badge>}
    {l.area === "outlet" && l.quantity === 1 && <Badge testid="badge-last-piece" className={`${extra} bg-[#FFF0EE] text-[#E0533F]`}><Flame className="h-3 w-3" />{t("card.lastPiece")}</Badge>}
  </>
  );
};

export const ListingCard = ({ l }) => {
  const { user, favIds, toggleFav } = useAuth();
  const { t, locale } = useI18n();
  const nav = useNavigate();
  const fav = favIds.has(l.listing_id);
  const onFav = (e) => {
    e.preventDefault();
    if (!user) return nav("/accedi");
    toggleFav(l.listing_id);
  };
  const cond = l.outlet_grade ? t(`grade.${l.outlet_grade}`) : t(`cond.${l.condition}`);
  return (
    <Link to={`/annuncio/${l.listing_id}`} data-testid={`listing-card-${l.listing_id}`} className="card-soft group flex flex-col overflow-hidden">
      <div className="relative aspect-[4/3] overflow-hidden bg-[#F1F4F9]">
        <img src={l.images?.[0]?.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${l.images[0]}` : l.images?.[0]} alt={l.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
        <div className="absolute left-2.5 top-2.5 flex max-w-[80%] flex-wrap gap-1"><ListingBadges l={l} t={t} compact /></div>
        {!(user && ["admin", "moderator", "operator"].includes(user.role)) && (
          <button onClick={onFav} data-testid={`fav-btn-${l.listing_id}`} aria-label="Preferito"
            className="absolute right-2.5 top-2.5 flex h-9 w-9 items-center justify-center rounded-full bg-white/95 shadow-sm transition-transform hover:scale-110">
            <Heart className={`h-4 w-4 ${fav ? "fill-[#FF7A6B] text-[#FF7A6B]" : "text-[#1E1B2E]"}`} />
          </button>
        )}
        <span className="absolute bottom-2.5 left-2.5 rounded-full bg-white/95 px-2.5 py-0.5 text-[11px] font-semibold text-[#0E7C66]">{cond}</span>
      </div>
      <div className="flex flex-1 flex-col p-4">
        <h3 className="line-clamp-1 text-[15px] font-semibold text-[#1E1B2E]">{l.title}</h3>
        <div className="mt-1.5 flex items-baseline gap-2">
          <span className="font-display text-xl font-bold text-[#3B1272]" data-testid="listing-card-price">{formatPrice(l.price_cents, locale)}</span>
          {l.compare_at_cents && <span className="text-xs text-[#94A3B8] line-through">{formatPrice(l.compare_at_cents, locale)}</span>}
        </div>
        <div className="mt-auto flex items-center justify-between pt-3 text-xs text-[#64748B]">
          <span className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{l.location?.city}{l.distance_m != null && ` · ${formatKm(l.distance_m)}`}</span>
          {l.accepts_exchange && <span className="flex items-center gap-1 font-semibold text-[#581C9C]"><Repeat className="h-3.5 w-3.5" />{t("card.exchange")}</span>}
        </div>
        <p className="mt-2 truncate text-xs text-[#94A3B8]">{l.seller_type === "juppo" ? "Juppo Outlet" : `${l.seller?.display_name} · ${t(`seller.${l.seller_type}`)}`}</p>
      </div>
    </Link>
  );
};

export const ListingSkeleton = () => (
  <div className="card-soft overflow-hidden" data-testid="listing-skeleton">
    <div className="aspect-[4/3] animate-pulse bg-[#EEF0F6]" />
    <div className="space-y-2 p-4"><div className="h-4 w-3/4 animate-pulse rounded bg-[#EEF0F6]" /><div className="h-5 w-1/3 animate-pulse rounded bg-[#EEF0F6]" /></div>
  </div>
);
